import { memo, useMemo } from "react"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
	IconCheckOutline18 as Check,
	IconFolderOutline18 as Folder,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { IconButton, NucleoFillIcon } from "@productlane/ui"
import CustomChevron from "@productlane/ui/src/components/CustomChevron"
import {
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisIcon } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"

import type { HiddenTier } from "./hidden-tier-folders"
import type { ArticleWithSubpages, GroupWithArticles } from "./types"
import { RoleCheck } from "@/components/shared/role-check"
import { useZero } from "@/lib/zero"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { getArticleDisplayTitle } from "./article-kind"
import { ArticleStatePill } from "./article-state-pill"
import { useHelpCenterNav } from "./help-center-context"
import {
	folderChoicesFor,
	hiddenFolderKey,
	planFolderMove,
} from "./hidden-tier-folders"
import { NavRowActions } from "./nav-row-actions"
import { translatedName, translatedTitle } from "./translated-title"
import { useArticleDelete } from "./use-article-delete"

// A link has no article header to hang a menu off, so the row is the only place
// it can be deleted from.
export const HiddenRowMenu = memo(
	({
		article,
		title,
		className,
		isSelected,
		articleGroups,
		tier,
	}: {
		article: ArticleWithSubpages
		title: string
		className?: string
		isSelected?: boolean
		articleGroups?: readonly GroupWithArticles[]
		tier?: HiddenTier
	}) => {
		const { dialog, requestDelete } = useArticleDelete({
			id: article.id,
			title,
			kind: article.kind,
		})
		const zero = useZero()
		const openGroup = useDocNavStore((s) => s.openGroup)
		const { selectedLanguage } = useHelpCenterNav()

		const folders = folderChoicesFor(article, tier, articleGroups ?? [])

		const moveToFolder = (target: GroupWithArticles) => {
			if (!tier || target.id === article.helpCenterGroupId) return
			void zero.mutate(
				mutators.helpCenterArticles.move(planFolderMove(article, target)),
			)
			openGroup(hiddenFolderKey(tier, target.id))
		}

		return (
			<RoleCheck not={["VIEWER"]}>
				{dialog}
				<NavRowActions isSelected={isSelected} className={className}>
					<DropdownMenu
						contentProps={{ align: "end", className: "w-fit pr-1" }}
						trigger={
							<IconButton
								Icon={EllipsisIcon}
								variant="ghost"
								size="md"
								type="button"
								aria-label="More actions"
								iconClassName="fill-current"
								className="relative hover:bg-gray-200 dark:hover:bg-gray-700"
							/>
						}
					>
						{folders.length > 0 && (
							<DropdownMenuSub>
								<DropdownMenuSubTrigger className="gap-3 px-2 text-secondary">
									<Folder className="h-4 w-4 flex-none" />
									Move to folder
								</DropdownMenuSubTrigger>
								<DropdownMenuPortal>
									<DropdownMenuSubContent>
										{folders.map((group) => (
											<DropdownMenuItem
												key={group.id}
												Icon={
													group.id === article.helpCenterGroupId
														? Check
														: undefined
												}
												onClick={() => moveToFolder(group)}
											>
												{translatedName(group, selectedLanguage)}
											</DropdownMenuItem>
										))}
									</DropdownMenuSubContent>
								</DropdownMenuPortal>
							</DropdownMenuSub>
						)}
						<DropdownMenuItem Icon={Trash} onClick={requestDelete}>
							Delete
						</DropdownMenuItem>
					</DropdownMenu>
				</NavRowActions>
			</RoleCheck>
		)
	},
)

HiddenRowMenu.displayName = "HiddenRowMenu"

export const HiddenArticleRow = memo(
	({
		article,
		articleGroups,
		tier,
	}: {
		article: ArticleWithSubpages
		articleGroups?: readonly GroupWithArticles[]
		tier?: HiddenTier
	}) => {
		const navigate = useNavigate()
		const params = useParams({ strict: false })
		const { onItemSelected, selectedLanguage } = useHelpCenterNav()
		const isExpanded = useDocNavStore((s) =>
			s.openArticleIds.includes(article.id),
		)
		const toggleArticle = useDocNavStore((s) => s.toggleArticle)

		const subpages = useMemo(() => article.subpages ?? [], [article.subpages])
		const title = getArticleDisplayTitle(
			article,
			translatedTitle(article, selectedLanguage),
		)

		const open = () => {
			void navigate({ to: "/edit-docs/$id", params: { id: article.id } })
			onItemSelected()
		}

		return (
			<li>
				<div
					className={cn(
						"group/item relative flex w-full min-w-0 items-center rounded-md",
						params.id === article.id
							? "bg-gray-100 text-primary dark:bg-gray-800"
							: "text-secondary hover:bg-gray-100 hover:text-primary hover:dark:bg-gray-800/50",
					)}
				>
					<div
						role="button"
						tabIndex={0}
						title={title}
						onClick={open}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault()
								open()
							}
						}}
						className="flex h-[1.9rem] w-full min-w-0 cursor-default items-center gap-2 rounded-md pl-2 pr-1 text-[13px] font-medium focus-visible:bg-gray-100 focus-visible:outline-none dark:focus-visible:bg-gray-800/50"
					>
						<NucleoFillIcon
							icon={article.icon}
							className="size-3.5 shrink-0 text-secondary opacity-90"
						/>
						<span className="min-w-0 flex-1 truncate text-ellipsis text-start opacity-90">
							{title}
						</span>
						<ArticleStatePill
							article={article}
							hideVisibility
							className="pointer-events-none flex-shrink-0 opacity-100"
						/>
						{subpages.length > 0 && (
							<button
								type="button"
								aria-label={
									isExpanded ? "Collapse subpages" : "Expand subpages"
								}
								aria-expanded={isExpanded}
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									toggleArticle(article.id)
								}}
								className="flex h-4 w-4 flex-none items-center justify-center rounded"
							>
								<CustomChevron
									width={8}
									height={8}
									strokeWidth={2.25}
									className={cn(
										"transform-gpu text-tertiary transition",
										isExpanded ? "rotate-90" : "rotate-0",
									)}
								/>
							</button>
						)}
					</div>
					<HiddenRowMenu
						article={article}
						title={title}
						isSelected={params.id === article.id}
						articleGroups={articleGroups}
						tier={tier}
						className={subpages.length > 0 ? "right-6" : "right-1"}
					/>
				</div>

				{isExpanded && subpages.length > 0 && (
					<ul className="flex list-none flex-col gap-px pl-4">
						{subpages.map((subpage) => {
							const subTitle = getArticleDisplayTitle(
								subpage,
								translatedTitle(subpage, selectedLanguage),
							)
							return (
								<li key={subpage.id}>
									<div
										className={cn(
											"group/item relative flex w-full min-w-0 items-center rounded-md",
											params.id === subpage.id
												? "bg-gray-100 text-primary dark:bg-gray-800"
												: "text-secondary hover:bg-gray-100 hover:text-primary hover:dark:bg-gray-800/50",
										)}
									>
										<div
											role="button"
											tabIndex={0}
											title={subTitle}
											onClick={() => {
												void navigate({
													to: "/edit-docs/$id",
													params: { id: subpage.id },
												})
												onItemSelected()
											}}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault()
													void navigate({
														to: "/edit-docs/$id",
														params: { id: subpage.id },
													})
													onItemSelected()
												}
											}}
											className="flex h-[1.8rem] w-full min-w-0 cursor-default items-center gap-2 rounded-md pl-2 pr-1 text-[13px] font-medium focus-visible:bg-gray-100 focus-visible:outline-none dark:focus-visible:bg-gray-800/50"
										>
											<NucleoFillIcon
												icon={subpage.icon}
												className="size-3.5 shrink-0 text-secondary opacity-90"
											/>
											<span className="min-w-0 flex-1 truncate text-ellipsis text-start opacity-90">
												{subTitle}
											</span>
											<ArticleStatePill
												article={subpage}
												hideVisibility
												className="flex-shrink-0"
											/>
										</div>
										<HiddenRowMenu
											article={subpage}
											title={subTitle}
											isSelected={params.id === subpage.id}
											className="right-1"
										/>
									</div>
								</li>
							)
						})}
					</ul>
				)}
			</li>
		)
	},
)

HiddenArticleRow.displayName = "HiddenArticleRow"
